<template>
  <div
    id="task-panel"
    :class="[activePanel === 'searchRoadshow' && 'is-active']"
    class="right-panel-wrapper is-task"
  >
    <div class="panel-overlay" @click="activePanel = 'none'"></div>
    <div class="right-panel">
      <div class="right-panel-head">
        <h3>{{ t('title-reports-panel') }}</h3>
        <transition name="fade-fast">
          <V-Button
            icon="fas fa-check"
            color="primary"
            raised
            class="btn-choose"
            :loading="props.loading"
            @click="handleConfirmSelect"
            v-if="selectedItemsCount > 0"
          >
            <span>{{ t('action-select') }}</span>
          </V-Button>
        </transition>
        <a class="close-panel" @click="activePanel = 'none'">
          <i
            aria-hidden="true"
            class="iconify"
            data-icon="feather:chevron-right"
          ></i>
        </a>
      </div>
      <div class="right-panel-body has-slimscroll" data-simplebar>
        <div class="search-layout">
          <transition name="fade-fast">
            <VDropdown
              ref="selectList"
              v-if="selectedItemsCount > 0"
              class="select-button"
            >
              <template #button="{ open, toggle }">
                <VIconBox color="primary" rounded @click="toggle">
                  <i class="fas fa-shopping-cart"></i>
                  <div class="badge">{{ selectedItemsCount }}</div>
                </VIconBox>
              </template>
              <template #content="{ close }">
                <a
                  v-for="item in selectedContents"
                  role="menuitem"
                  :key="item.id + item.name"
                  class="dropdown-item is-media"
                >
                  <i class="fas fa-box" aria-hidden="true"></i>
                  <p class="item-title">{{ item.name }}</p>
                  <i
                    class="fas fa-trash-alt item-delete"
                    @click="removeSelectedContent(item)"
                  ></i>
                </a>
              </template>
            </VDropdown>
          </transition>
          <transition name="fade-fast">
            <V-Control icon="feather:search" class="search-input">
              <input
                ref="keywordsInput"
                v-model="searchKeywords"
                class="input is-rounded search-input"
                @keyup.enter="handleSearch"
                :placeholder="t('placeholder-search')"
              />
            </V-Control>
          </transition>
        </div>
        <div>
					<roadshow-simple-list
						:data="roadshowResponse&&roadshowResponse.list"
						:totalCount="roadshowResponse&&roadshowResponse.count"
						:page-size="searchParam.limit"
						:loading="isLoading"
						:page="reportPage"
						:page-link-display="5"
						:show-empty-placeholder="!inputingValue && searchParam.keywords !== ''"
						:isMultiSelect="props.isMultiSelect"
						@itemClick="handleContentClick"
						@onPageChange="onsearchRoadshowPageChange"
					/>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, reactive, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { activePanel } from '/@src/state/activePanelState'
import { SearchRequestParam, ItemsResponse, ContentKind } from '/@src/models/api'
import { Content, LinkContent, ContentStatusValue } from '/@src/models/content'
import useContents from "/@src/composable/content/useContents";
import useRoadshowList from "/@src/composable/content/useRoadshowList";
import useMediaFiles from "/@src/composable/media/useMediaFiles";
const emit = defineEmits<{
  (event: 'onSelectContents', data: Array<Content>): void
}>()

const props = defineProps({
  isMultiSelect: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * 是否状态必须为已发布
   */
  requirePublish: {
    type: Boolean,
    default: false,
  },
})
const propsData = toRefs(props)

const selectedItemsCount = computed(() => {
  return selectedContents.length
})
const videoPage = ref(1)
const reportPage = ref(1)
const linkPage = ref(1)
const searchKeywords = ref('')
const isLoading = ref(false)
const inputingValue = ref(false)
const {queryContents,contentListResponse} = useContents()
const {getRoadshowList,updateStatus,roadshowResponse} = useRoadshowList()
const {getReposts,filesResponse} = useMediaFiles()
const linksData = ref<ItemsResponse<LinkContent>>()
const searchParam: SearchRequestParam = reactive({
  keywords: '',
  limit: 10,
  page: 1,
  anchor: '',
})
const selectedContents = reactive<Array<any>>([])

watch(activePanel, async (nv) => {
  if (nv === 'searchRoadshow') {
    handleSearch()
  }
})
watch(propsData.isMultiSelect, () => {
  clearSelectedContents()
})

const handleSearch = async (page) => {
	isLoading.value = true
  inputingValue.value = false
  searchParam.keywords = searchKeywords.value
	await getRoadshowList({
		limit:searchParam.limit,
		page:reportPage.value,
		keywords:searchKeywords.value,
	})
	if(props.isMultiSelect){
		checkSelectState(filesResponse.value!!.list)
	}
  isLoading.value = false
}

const onsearchRoadshowPageChange = (page: number) => {
  reportPage.value = page
  handleSearch()
}

const handleContentClick = (item: Content) => {
  if (props.isMultiSelect) {
    toggleContentSelect(item)
  } else {
    singleContentSelect(item)
  }
}
const toggleContentSelect = (item: Content) => {
  item.select = !item.select
  if (item.select) {
    selectedContents.unshift(item)
  } else {
    removeSelectedContent(item)
  }
}
const singleContentSelect = (item: Content) => {
  item.select = !item.select
  if (item.select) {
    if (selectedContents[0] && selectedContents[0] !== item) {
      selectedContents[0].select = false
      selectedContents.splice(0, 1)
    }
    selectedContents.push(item)
  } else {
    selectedContents.splice(0, 1)
  }
}
const removeSelectedContent = (item: Content) => {
  let find = -1
  for (let index = 0; index < selectedContents.length; index++) {
    const data = selectedContents[index]
    if (data.id === item.id && data.kind === item.kind) {
      item.select = false
      find = index
      break
    }
  }
  if (find >= 0) {
    selectedContents.splice(find, 1)
  }
}

const handleConfirmSelect = () => {
  const content = JSON.parse(JSON.stringify(selectedContents))
  emit('onSelectContents',content)
  activePanel.value = 'none'
  clearSelectedContents()
}

const clearSelectedContents = () => {
  if (selectedContents.length > 0) {
    selectedContents.forEach((it) => {
      it.select = false
    })
  }
  selectedContents.splice(0, selectedContents.length)
}
defineExpose({ clearSelectedContents })
</script>

<style lang="scss">
@import '../../../scss/abstracts/_variables.scss';
@import '../../../scss/abstracts/_mixins.scss';
@import '../../../scss/layout/_right-panel.scss';
.right-panel-wrapper {
  .right-panel-head {
    .btn-choose {
      margin-left: auto;
    }
  }
  .right-panel-body {
    .search-layout {
      display: flex;
      .select-button {
        cursor: pointer;
        margin-right: 10px;
        .fas {
          font-size: 14px;
        }
        .dropdown-item {
          display: flex;
          width: 20rem;
          align-items: center;
          padding-right: 8px;
          .item-title {
            @include textLine(1);

            color: var(--light-text);
            font-weight: 400;
            font-size: 0.8rem;
            margin-left: 4px;
            padding-right: 4px;
          }
          .item-delete {
            margin-left: auto;
            padding: 4px;
            //color: var(--danger);
          }
        }
        .dropdown-item:hover .item-title {
          color: var(--dark-text);
        }
      }
      .badge {
        position: absolute;
        width: 18px;
        height: 18px;
        background: var(--danger);
        top: 1px;
        right: -5px;
        text-align: center;
        font-size: 11px;
        font-weight: 500;
        color: white;
        box-shadow: var(--light-box-shadow);
        border-radius: var(--radius-rounded);
      }
      .search-input {
        flex: 1;
        transition: width 1s linear;
        margin-bottom: 10px;
      }
    }
  }
}
</style>
